/**
 * Row for a report.
 */
import * as React from 'react';
import { Calendar, Clock, User, Users } from 'lucide-react';
import { Button } from '@/components/forms/Button';
import { LabelBadge } from '@/components/common/LabelBadge';
import { TableRow } from '@/components/common/data-table';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  REMOTE_REPORT_STATUS_CONFIG,
  REMOTE_REPORT_SCORING_STATUS_CONFIG,
  REMOTE_REPORT_SESSION_TYPE_CONFIG,
  type PerTypeReportPageActions,
} from '@/components/reports/report-configs';
import type { ReportListItem, ReportTab as Tab } from '@/types/remote/report.types';
import {
  deriveReportParticipants,
  getReportActionState,
  resolveReportPermissions,
  formatReportSessionDate,
  formatReportLastModified,
  isAllReportsTab,
  isInformationReportTab,
  isMeetingReportTab,
  isTutoringReportTab,
} from './report-table-utils';

/** Avatar plus name(s) for students shown on a remote report row. */
function buildStudentsCell(
  students: ReturnType<typeof deriveReportParticipants>['students'],
): React.ReactNode {
  if (students.length === 0) {
    return <span className="text-muted-foreground">—</span>;
  }
  if (students.length === 1) {
    return (
      <div className="flex items-center gap-2">
        <div
          className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-medium"
          style={{ backgroundColor: 'var(--color-2-nuance-1)' }}
        >
          {students[0].displayName?.[0] || 'E'}
        </div>
        <span className="text-foreground">{students[0].displayName}</span>
      </div>
    );
  }
  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="flex items-center gap-1.5 text-foreground cursor-default">
            <Users className="w-3.5 h-3.5 text-[var(--color-1-nuance-1)] shrink-0" />
            <span className="text-sm">
              {students.length} élève{students.length > 1 ? 's' : ''}
            </span>
          </div>
        </TooltipTrigger>
        <TooltipContent>
          <div className="space-y-0.5">
            {students.map((s) => (
              <p key={s.userId}>{s.displayName}</p>
            ))}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

/** Props for {@link ReportRow}. */
interface ReportRowProps {
  report: ReportListItem;
  activeTab: Tab;
  onEdit?: (id: string) => void;
  onView?: (id: string) => void;
  onDelete?: (id: string) => void;
  onValidate?: (id: string) => void;
  pageActions?: PerTypeReportPageActions;
}

/** Table row for a remote report. */
export const ReportRow: React.FC<ReportRowProps> = ({
  report,
  activeTab,
  onEdit,
  onView,
  onDelete,
  onValidate,
  pageActions,
}) => {
  const { canEdit, canValidate } = resolveReportPermissions(report.sessionType, pageActions);
  const canDelete = pageActions?.canDeleteRow ?? true;

  const reportStatusConfig = REMOTE_REPORT_STATUS_CONFIG[report.compteRenduStatus];
  const scoringStatusConfig = REMOTE_REPORT_SCORING_STATUS_CONFIG[report.scoringStatus];
  const sessionTypeConfig = REMOTE_REPORT_SESSION_TYPE_CONFIG[report.sessionType];

  const { tutors, students, animator, infoParticipants, welcomeParticipants } =
    deriveReportParticipants(report);

  const { editDisabled, editTooltip, validateDisabled, validateTooltip } =
    getReportActionState(report);

  const sessionDisplayText = formatReportSessionDate(report.sessionDate);
  const lastModified = formatReportLastModified(report.lastModifiedAt);

  const meetingParticipants = isInformationReportTab(activeTab)
    ? infoParticipants
    : welcomeParticipants;
  let participantsCell: React.ReactNode;
  if (meetingParticipants.length === 0) {
    participantsCell = (
      <div className="flex items-center gap-1.5">
        <Users className="w-3.5 h-3.5 text-[var(--color-1-nuance-1)] shrink-0" />
        <span className="text-sm">0 participant</span>
      </div>
    );
  } else if (meetingParticipants.length === 1) {
    participantsCell = (
      <div className="flex items-center gap-2">
        <div
          className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-medium"
          style={{ backgroundColor: 'var(--color-2-nuance-1)' }}
        >
          {meetingParticipants[0].displayName?.[0] || 'P'}
        </div>
        <span className="text-foreground">{meetingParticipants[0].displayName}</span>
      </div>
    );
  } else {
    participantsCell = (
      <TooltipProvider delayDuration={200}>
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="flex items-center gap-1.5 text-foreground cursor-default">
              <Users className="w-3.5 h-3.5 text-[var(--color-1-nuance-1)] shrink-0" />
              <span className="text-sm">
                {meetingParticipants.length} participant{meetingParticipants.length > 1 ? 's' : ''}
              </span>
            </div>
          </TooltipTrigger>
          <TooltipContent>
            <div className="space-y-0.5">
              {meetingParticipants.map((p) => (
                <p key={p.userId}>{p.displayName}</p>
              ))}
            </div>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  const studentsCell = buildStudentsCell(students);

  return (
    <TableRow>
      <td>
        <div className="flex items-center gap-2">
          <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-[color-mix(in_srgb,var(--color-1-nuance-1)_10%,transparent)]">
            <Calendar className="w-4 h-4 text-[var(--color-1-nuance-1)]" />
          </div>
          <div className="flex flex-col gap-0.5">
            <span className="font-medium text-foreground">{sessionDisplayText}</span>
            {report.sessionStartTime && report.sessionEndTime && (
              <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                <Clock className="w-3.5 h-3.5 shrink-0 text-[var(--color-1-nuance-1)]" />
                <span>
                  {report.sessionStartTime.slice(0, 5)} - {report.sessionEndTime.slice(0, 5)}
                </span>
              </div>
            )}
          </div>
        </div>
      </td>

      {isAllReportsTab(activeTab) && (
        <td className="text-center">
          {sessionTypeConfig && (
            <LabelBadge
              label={sessionTypeConfig.label}
              icon={sessionTypeConfig.icon}
              variant="ghost"
              color={sessionTypeConfig.badgeColor}
              size="sm"
            />
          )}
        </td>
      )}

      {isTutoringReportTab(activeTab) && (
        <>
          <td>
            {tutors.length > 0 ? (
              <div className="flex items-center gap-2">
                <div
                  className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-medium"
                  style={{ backgroundColor: 'var(--color-1-nuance-1)' }}
                >
                  {tutors[0].displayName?.[0] || 'T'}
                </div>
                <span className="text-foreground">{tutors[0].displayName}</span>
              </div>
            ) : (
              <span className="text-muted-foreground">—</span>
            )}
          </td>
          <td>{studentsCell}</td>
          <td className="text-center">
            {sessionTypeConfig && (
              <LabelBadge
                label={sessionTypeConfig.label}
                icon={sessionTypeConfig.icon}
                variant="ghost"
                color={sessionTypeConfig.badgeColor}
                size="sm"
              />
            )}
          </td>
        </>
      )}

      {isMeetingReportTab(activeTab) && (
        <>
          <td>
            {animator ? (
              <div className="flex items-center gap-2">
                <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-[color-mix(in_srgb,var(--color-1-nuance-1)_10%,transparent)]">
                  <User className="w-4 h-4 text-[var(--color-1-nuance-1)]" />
                </div>
                <span className="text-foreground">{animator.displayName}</span>
              </div>
            ) : (
              <div className="flex items-center gap-1.5">
                <User className="w-3.5 h-3.5 text-[var(--color-1-nuance-1)] shrink-0" />
                <span className="text-sm">0 animateur</span>
              </div>
            )}
          </td>
          <td>{participantsCell}</td>
        </>
      )}

      <td className="text-center">
        {reportStatusConfig && (
          <LabelBadge
            label={reportStatusConfig.label}
            icon={reportStatusConfig.icon}
            variant={reportStatusConfig.badgeVariant ?? 'ghost'}
            color={reportStatusConfig.badgeColor}
            size="sm"
          />
        )}
      </td>

      <td className="text-center">
        {scoringStatusConfig && (
          <LabelBadge
            label={scoringStatusConfig.label}
            icon={scoringStatusConfig.icon}
            variant={scoringStatusConfig.badgeVariant ?? 'ghost'}
            color={scoringStatusConfig.badgeColor}
            size="sm"
          />
        )}
      </td>

      <td className="text-sm text-foreground">{lastModified}</td>

      <td className="text-center">
        <div className="flex items-center justify-center gap-1">
          {onView && <Button preset="row-view" onClick={() => onView(report.id)} />}

          {canEdit && onEdit && (
            <Button
              preset="row-edit"
              onClick={() => onEdit(report.id)}
              disabled={editDisabled}
              tooltipText={editTooltip}
            />
          )}

          {canValidate && onValidate && (
            <Button
              preset="row-validate"
              onClick={() => onValidate(report.id)}
              disabled={validateDisabled}
              tooltipText={validateTooltip}
            />
          )}

          {canDelete && onDelete && (
            <Button preset="row-delete" onClick={() => onDelete(report.id)} />
          )}
        </div>
      </td>
    </TableRow>
  );
};
